import React from 'react';
import { Button, CircularProgress, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ForecastChangedProduct } from '@domain/tasks/models/Task';
import { APP_CONFIG } from '../../../../../config/app.config';
import { Icon, IconSize } from '@shared/icons';
import DashboardStatusChip from '../../general/DashboardStatusChip';
import { orderShiftLabel, orderWithinValueLabel } from '../utils';
import { STOCK_LEVEL_LABEL, stockTone } from '../../products/utils/stock';
import type { StockLevel } from '../../products/utils/types';

interface ForecastChangedMetricsProps {
  product: ForecastChangedProduct | null;
  isAcknowledging?: boolean;
  alreadyChecked?: boolean;
  onMarkChecked: () => void;
}

const ForecastChangedMetrics: React.FC<ForecastChangedMetricsProps> = ({
  product,
  isAcknowledging,
  alreadyChecked,
  onMarkChecked,
}) => {
  const colors = APP_CONFIG.ui.theme.colors;
  const salesLabel =
    product?.averageWeeklySales != null ? `${product.averageWeeklySales} sales` : null;
  const inventoryLabel = product?.quantity != null ? `${product.quantity} items` : null;
  const stockLevel = product ? taskStockLevel(product) : null;
  const withinLabel =
    product?.orderWithinDays != null ? orderWithinValueLabel(product.orderWithinDays) : '';

  return (
    <Stack
      direction="row"
      alignItems="center"
      justifyContent="space-between"
      gap={2}
      flexWrap="wrap"
    >
      <Stack direction="row" alignItems="center" gap={6} flexWrap="wrap">
        {salesLabel && (
          <MetricStat
            label="Average forecast per week"
            value={salesLabel}
            chip={
              product?.changePercent != null ? (
                <DashboardStatusChip
                  label={`${product.changePercent >= 0 ? '+' : ''}${product.changePercent}%`}
                  tone={product.changePercent >= 0 ? 'success' : 'error'}
                />
              ) : undefined
            }
          />
        )}
        {inventoryLabel && (
          <MetricStat
            label="Inventory"
            value={inventoryLabel}
            chip={
              stockLevel ? (
                <DashboardStatusChip
                  label={STOCK_LEVEL_LABEL[stockLevel]}
                  tone={stockTone(stockLevel)}
                />
              ) : undefined
            }
          />
        )}
        {withinLabel && (
          <MetricStat
            label="Order within"
            value={withinLabel}
            chip={
              product?.orderShiftDays != null ? (
                <DashboardStatusChip
                  label={orderShiftLabel(product.orderShiftDays)}
                  tone={product.orderShiftDays < 0 ? 'warning' : 'success'}
                />
              ) : undefined
            }
          />
        )}
      </Stack>

      <Button
        variant="outlined"
        size="small"
        onClick={onMarkChecked}
        disabled={isAcknowledging || alreadyChecked}
        startIcon={
          isAcknowledging ? (
            <CircularProgress color="inherit" size={16} />
          ) : (
            <Icon icon="Check" size={IconSize.MEDIUM} color={colors.primary} />
          )
        }
      >
        Mark as checked
      </Button>
    </Stack>
  );
};

const MetricStat: React.FC<{
  label: string;
  value: string;
  chip?: React.ReactNode;
}> = ({ label, value, chip }) => {
  return (
    <Stack>
      <Typography
        variant="caption"
        color="secondary"
      >
        {label}
      </Typography>

      <Stack direction="row" alignItems="center" gap={1}>
        <Typography variant="h5"   >
          {value}
        </Typography>
        {chip}
      </Stack>
    </Stack>
  );
};

const taskStockLevel = (product: ForecastChangedProduct): StockLevel => {
  if ((product.quantity ?? 0) <= 0) return 'out_of_stock';
  if (product.orderWithinDays == null && !product.currentWeekly?.length) {
    return 'unknown';
  }
  if (product.orderWithinDays != null && product.orderWithinDays <= 0) {
    return 'low_stock';
  }
  return 'in_stock';
};

export default ForecastChangedMetrics;
